import { useAuth } from '@/hooks'
import { CategoriaEnum } from '@acme/auth'
import {
    Box,
    Checkbox,
    ListItemText,
    SelectChangeEvent,
    TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import { FormField } from 'global/components/Form'
import React, { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { funcionario } from 'service/api'
import { useStore } from 'store'
import Menu from '../MenuV2'
import { filterLocaisCentro } from './filter-select'
import { SelectProps } from './types'

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
}

type Props = Omit<
    SelectProps,
    'buttom' | 'isSupervisor' | 'form' | 'defaultValue'
> & {
    checked?: number[]
    onChange?: (ids: number[]) => void
    defaultValues?: number[]
    /** Utiliza filtro de locais e centro de custo */
    useFilter?: boolean
    value?: number[]
    form?: boolean | string
}

export const SelectFuncionarios: React.FC<Props> = ({
    size = 'small',
    form,
    children,
    disabled,
    checked,
    onChange,
    defaultValues = [],
    useFilter = false,
    value,
    width = 260,
    ...props
}) => {
    const formName = typeof form === 'string' ? form : 'funcionarioIds'
    const {
        decoded: { categoria },
    } = useAuth()
    const formContext = useFormContext()
    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const locais = useStore(state => state.locaisIds)
    const [competencia, competenciaAtual] = useStore(state => [
        state.competencia,
        state.competenciaAtual,
    ])
    const diasRescisao = useStore(state => state.company?.diasRescisao)

    const { data: funcionarios = [] } = funcionario.useFetch({
        queryParams: { empresaId },
    })

    const [_employeeIds, _setEmployeeIds] = useState<number[]>(defaultValues)
    const employeeIds = value ? value : _employeeIds
    const setEmployeeIds = (props: number[]) => {
        _setEmployeeIds(props)
        onChange?.(props)
    }

    const exibirApenasAtivos = categoria !== CategoriaEnum.Funcionario
    const data = React.useMemo(
        () =>
            funcionarios.filter(obj => {
                if (exibirApenasAtivos && obj.dtRescisao) {
                    const data =
                            competencia === competenciaAtual
                                ? dayjs()
                                : dayjs(competencia).startOf('month'),
                        tolerancia = diasRescisao ?? 40

                    if (
                        data
                            .add(tolerancia, 'day')
                            .isAfter(dayjs(obj.dtRescisao), 'day')
                    )
                        return false
                }

                if (useFilter === false) return true
                return filterLocaisCentro<typeof obj>(
                    locais,
                    [centroCustoId!],
                    it => it,
                )(obj)
            }),
        [
            funcionarios,
            exibirApenasAtivos,
            centroCustoId,
            competencia,
            competenciaAtual,
            diasRescisao,
            locais,
        ],
    )

    useEffect(() => {
        if (useFilter && (centroCustoId > 0 || locais.length))
            setEmployeeIds([])
    }, [centroCustoId, locais, useFilter])

    const isEverythingSelected =
        employeeIds.length === data.length + (checked?.length || 0)

    const handleChange = (event: SelectChangeEvent<unknown>) => {
        const {
            target: { value },
        } = event

        if ((value as any[]).includes('todos')) {
            if (isEverythingSelected) {
                setEmployeeIds([])
            } else {
                const ids = data
                    .map(({ id }) => id)
                    .filter(id => !checked?.includes(id))

                setEmployeeIds(ids || [])
                onChange?.(ids || [])
            }
        } else {
            setEmployeeIds(value as number[])
            onChange?.(value as number[])
        }
    }

    useEffect(() => {
        if (form && formContext) {
            formContext.setValue(formName, employeeIds)
        }
    }, [employeeIds])

    return (
        <Box display="flex" alignItems="center" gap={1} width={width}>
            {form ? (
                <FormField
                    name={formName}
                    label="Funcionários"
                    size={size}
                    select
                    disabled={disabled}
                    helperText=" "
                    fullWidth
                    {...props}
                    SelectProps={{
                        multiple: true,
                        value: employeeIds,
                        onChange: handleChange,
                        renderValue: selected => {
                            return data
                                .filter(({ id }) => !checked?.includes(id))
                                .filter(it =>
                                    (selected as number[]).some(
                                        select => select === it.id,
                                    ),
                                )
                                .map(({ nome }) => nome)
                                .join(', ')
                        },
                        MenuProps,
                    }}
                >
                    {children}
                    <Menu.Item key="employee-todos" value="todos">
                        <Checkbox checked={isEverythingSelected} />
                        <ListItemText primary="Todos" />
                    </Menu.Item>
                    {data.map(({ id, nome }, index) => {
                        return (
                            <Menu.Item
                                key={`employee-${index}`}
                                value={id}
                                disabled={checked?.includes(id)}
                            >
                                <Checkbox
                                    checked={
                                        checked?.includes(id) ||
                                        employeeIds.some(it => it === id)
                                    }
                                />
                                <ListItemText primary={nome} />
                            </Menu.Item>
                        )
                    })}
                </FormField>
            ) : (
                <TextField
                    label="Funcionários"
                    variant="filled"
                    SelectProps={{
                        multiple: true,
                        value: employeeIds,
                        onChange: handleChange,
                        renderValue: selected => {
                            return data
                                .filter(({ id }) => !checked?.includes(id))
                                .filter(it =>
                                    (selected as number[]).some(
                                        select => select === it.id,
                                    ),
                                )
                                .map(({ nome }) => nome)
                                .join(', ')
                        },
                        MenuProps,
                    }}
                    select
                    fullWidth
                    disabled={disabled}
                    size={size}
                    {...props}
                >
                    {children}
                    {data.length > 0 && (
                        <Menu.Item key="employee-todos" value="todos">
                            <Checkbox />
                            <ListItemText primary="Todos" />
                        </Menu.Item>
                    )}
                    {data.map(({ id, nome }, index) => {
                        return (
                            <Menu.Item
                                key={`employee-${index}`}
                                value={id}
                                disabled={checked?.includes(id)}
                            >
                                <Checkbox
                                    checked={
                                        checked?.includes(id) ||
                                        employeeIds.some(it => it === id)
                                    }
                                />
                                <ListItemText primary={nome} />
                            </Menu.Item>
                        )
                    })}
                </TextField>
            )}
        </Box>
    )
}
